import { Badge } from 'src/design/atoms/Badge';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Drawer } from 'src/design/molecules/Drawer';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { ScimIntegrationDto } from 'src/api/FiretrolApiClient';
import { useScimUserMappings } from 'src/api/queries/admin/useScimIntegrations';
import { formatDateTime } from 'src/utils';

interface ScimMappingsDrawerProps {
  integration: ScimIntegrationDto | null;
  onClose: () => void;
}

const statusColor: Record<string, string> = {
  synced: 'green',
  error: 'red',
  pending: 'yellow',
  deleted: 'gray',
};

export const ScimMappingsDrawer: React.FC<ScimMappingsDrawerProps> = ({
  integration,
  onClose,
}) => {
  const mappingsQuery = useScimUserMappings(integration?.id ?? null);
  const mappings = mappingsQuery.data ?? [];
  const loading = mappingsQuery.isFetching;

  return (
    <Drawer
      opened={!!integration}
      onClose={onClose}
      title={`User Mappings — ${integration?.name || ''}`}
      position="right"
      size="70%"
    >
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />
        {mappings.length === 0 && !loading && (
          <Text c="dimmed" ta="center" mt="lg">
            No user mappings found for this integration.
          </Text>
        )}
        {mappings.length > 0 && (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Remote SCIM ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last Synced</Table.Th>
                <Table.Th>Last Error</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mappings.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.userName || '—'}</Table.Td>
                  <Table.Td>{m.userEmail || '—'}</Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {m.remoteScimId || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusColor[m.syncStatus] || 'gray'}>
                      {m.syncStatus}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {m.lastSyncedAt ? formatDateTime(m.lastSyncedAt) : '—'}
                  </Table.Td>
                  <Table.Td>
                    {m.lastError && (
                      <Text size="xs" c="red" lineClamp={2}>
                        {m.lastError}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Drawer>
  );
};
